/**
 * Signer ceremony — the page a recipient lands on when they click
 * the email link "/sign/:token".
 *
 * Flow:
 *   1. Fetch ceremony state from /api/sign/:token. Server marks the
 *      signer as 'viewed' on first hit and audits the access.
 *   2. Render the PDF in an <iframe> so the signer can scroll and
 *      visually confirm what they're about to sign.
 *   3. Below the PDF, render a field list. For signature/initials
 *      fields, embed a SignaturePad. For text/date/checkbox, render
 *      the appropriate input.
 *   4. Gate "Submit" behind the consent checkbox.
 *   5. POST /api/sign/:token/submit with field values + signature
 *      images. On success, show a "signed" thank-you screen.
 *
 * Decline path: a small "Decline" link below Submit posts to
 * /decline with an optional reason. Once declined, the envelope is
 * frozen and the dealer is notified.
 *
 * Coordinate handling lives server-side; this page submits raw
 * values + signature PNGs only.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Container, Typography, Paper, Button, Stack, Alert,
  CircularProgress, Checkbox, FormControlLabel, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip,
} from '@mui/material';
import SignaturePad, { SignaturePadHandle } from './components/SignaturePad';

const API_URL = 'https://api.autoelite.io';

interface CeremonyField {
  id: number;
  field_kind: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
  page: number;
  x: number; y: number; width: number; height: number;
  label: string | null;
  required: number;
}

interface CeremonyState {
  envelope: { id: number; subject: string; locale: 'en' | 'fr'; expires_at: string };
  signer: { position: number; name: string; role_label: string | null; status: string };
  fields: CeremonyField[];
  pdf_base64: string;
  consent_text_version: string;
  waiting_for_others: boolean;
  already_signed?: boolean;
}

const T = {
  en: {
    loading: 'Loading…',
    loadFailed: 'This signing link is invalid or has expired.',
    waitingTitle: 'Waiting for earlier signers',
    waitingBody: 'You will be able to sign as soon as the previous signers complete this document. We will email you when it is your turn.',
    alreadySigned: 'You have already signed this document.',
    title: 'Document to sign',
    role: 'Role',
    reviewLabel: 'Review the document',
    fieldsLabel: 'Fill in your information',
    sigLabel: 'Your signature',
    initialsLabel: 'Initials',
    dateLabel: 'Date',
    textLabel: 'Value',
    consent: 'I agree to do business electronically and that my electronic signature on this document is the legal equivalent of my handwritten signature.',
    submit: 'Sign and finalize',
    declineLink: 'I want to decline',
    declineTitle: 'Decline to sign',
    declineHelp: 'Tell the sender why (optional). Once you decline, this document is frozen and the dealer will be notified.',
    reason: 'Reason',
    cancel: 'Cancel',
    confirmDecline: 'Decline',
    submittedTitle: 'Thank you',
    submittedBody: 'Your signature has been recorded. The sender has been notified.',
    submittedFinalize: 'All signers have completed. A sealed copy has been generated.',
    poweredBy: 'Secured by AutoElite',
    signed: 'Signed',
    pending: 'Pending',
  },
  fr: {
    loading: 'Chargement…',
    loadFailed: 'Ce lien de signature est invalide ou expiré.',
    waitingTitle: 'En attente des signataires précédents',
    waitingBody: 'Vous pourrez signer dès que les signataires précédents auront terminé. Nous vous enverrons un courriel à votre tour.',
    alreadySigned: 'Vous avez déjà signé ce document.',
    title: 'Document à signer',
    role: 'Rôle',
    reviewLabel: 'Vérifier le document',
    fieldsLabel: 'Remplir vos informations',
    sigLabel: 'Votre signature',
    initialsLabel: 'Initiales',
    dateLabel: 'Date',
    textLabel: 'Valeur',
    consent: 'J’accepte de faire affaire électroniquement et que ma signature électronique sur ce document a la même valeur juridique qu’une signature manuscrite.',
    submit: 'Signer et finaliser',
    declineLink: 'Je veux refuser',
    declineTitle: 'Refuser de signer',
    declineHelp: 'Dites-nous pourquoi (facultatif). Une fois refusé, le document est gelé et le concessionnaire est avisé.',
    reason: 'Raison',
    cancel: 'Annuler',
    confirmDecline: 'Refuser',
    submittedTitle: 'Merci',
    submittedBody: 'Votre signature a été enregistrée. L’expéditeur en a été avisé.',
    submittedFinalize: 'Tous les signataires ont terminé. Une copie scellée a été générée.',
    poweredBy: 'Sécurisé par AutoElite',
    signed: 'Signé',
    pending: 'En attente',
  },
};

interface Props {
  token: string;
}

const SignerCeremony: React.FC<Props> = ({ token }) => {
  const [state, setState] = useState<CeremonyState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Per-field value state + per-field signature pad refs.
  const [textValues, setTextValues] = useState<Record<number, string>>({});
  const [checkboxValues, setCheckboxValues] = useState<Record<number, boolean>>({});
  const sigRefs = useRef<Record<number, SignaturePadHandle | null>>({});

  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ completed: boolean } | null>(null);

  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [declined, setDeclined] = useState(false);

  const locale: 'en' | 'fr' = state?.envelope.locale === 'fr' ? 'fr' : 'en';
  const t = T[locale];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/sign/${encodeURIComponent(token)}`);
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: '' }));
          if (!cancelled) setLoadError(err.error || `HTTP ${r.status}`);
          return;
        }
        const json = await r.json();
        if (cancelled) return;
        setState(json);

        // Auto-fill date fields with today in the envelope's locale.
        if (json.fields) {
          const now = new Date();
          const dateStr = now.toLocaleDateString(json.envelope.locale === 'fr' ? 'fr-CA' : 'en-CA', {
            year: 'numeric', month: 'long', day: 'numeric',
          });
          const initText: Record<number, string> = {};
          for (const f of json.fields as CeremonyField[]) {
            if (f.field_kind === 'date') initText[f.id] = dateStr;
            else if (f.field_kind === 'text') initText[f.id] = '';
          }
          setTextValues(initText);
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (!state) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const fieldsPayload = state.fields.map(f => {
        if (f.field_kind === 'signature' || f.field_kind === 'initials') {
          const png = sigRefs.current[f.id]?.exportPng() ?? null;
          return { id: f.id, signature_image_b64: png };
        }
        if (f.field_kind === 'checkbox') {
          return { id: f.id, value_text: checkboxValues[f.id] ? '1' : '0' };
        }
        return { id: f.id, value_text: textValues[f.id] ?? '' };
      });
      const r = await fetch(`${API_URL}/api/sign/${encodeURIComponent(token)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent_accepted: true, fields: fieldsPayload }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: '' }));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      const json = await r.json();
      setSubmitted({ completed: !!json.completed });
    } catch (e: any) {
      setSubmitError(e.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const decline = async () => {
    setDeclining(true);
    try {
      await fetch(`${API_URL}/api/sign/${encodeURIComponent(token)}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason || null }),
      });
      setDeclineOpen(false);
      setDeclined(true);
    } catch {
      // best-effort: server may have processed even if response failed
      setDeclineOpen(false);
      setDeclined(true);
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">{t.loading}</Typography>
      </Container>
    );
  }

  if (loadError || !state) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error">{loadError || t.loadFailed}</Alert>
      </Container>
    );
  }

  if (state.already_signed) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="success">{t.alreadySigned}</Alert>
      </Container>
    );
  }

  if (declined) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="warning">{locale === 'fr' ? 'Vous avez refusé de signer ce document.' : 'You have declined to sign this document.'}</Alert>
      </Container>
    );
  }

  if (state.waiting_for_others) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>{t.waitingTitle}</Typography>
          <Typography color="text.secondary">{t.waitingBody}</Typography>
        </Paper>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom color="primary">{t.submittedTitle}</Typography>
          <Typography sx={{ mb: 1 }}>{t.submittedBody}</Typography>
          {submitted.completed && <Typography color="text.secondary">{t.submittedFinalize}</Typography>}
        </Paper>
      </Container>
    );
  }

  const pdfDataUri = `data:application/pdf;base64,${state.pdf_base64}`;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography variant="h4" gutterBottom>{t.title}</Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>{state.envelope.subject}</Typography>
          {state.signer.role_label && (
            <Chip label={`${t.role}: ${state.signer.role_label}`} size="small" sx={{ mt: 1 }} />
          )}
        </Box>

        {/* PDF preview */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>{t.reviewLabel}</Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100' }}>
            <iframe
              title="document"
              src={pdfDataUri}
              style={{ width: '100%', height: '70vh', border: 0, display: 'block' }}
            />
          </Box>
        </Box>

        {/* Fields */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>{t.fieldsLabel}</Typography>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={3} divider={<Divider />}>
              {state.fields.map(f => (
                <FieldRow
                  key={f.id}
                  field={f}
                  locale={locale}
                  t={t}
                  textValue={textValues[f.id] || ''}
                  onTextChange={(v) => setTextValues(prev => ({ ...prev, [f.id]: v }))}
                  checkboxValue={!!checkboxValues[f.id]}
                  onCheckboxChange={(v) => setCheckboxValues(prev => ({ ...prev, [f.id]: v }))}
                  sigRef={(h) => { sigRefs.current[f.id] = h; }}
                />
              ))}
            </Stack>
          </Paper>
        </Box>

        {/* Consent + submit */}
        <Box>
          <FormControlLabel
            control={<Checkbox checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} />}
            label={t.consent}
          />
        </Box>

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Button color="inherit" onClick={() => setDeclineOpen(true)} disabled={submitting}>
            {t.declineLink}
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={submit}
            disabled={!consentChecked || submitting}
          >
            {submitting ? <CircularProgress size={22} sx={{ color: 'common.white' }} /> : t.submit}
          </Button>
        </Stack>

        <Typography variant="caption" align="center" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {t.poweredBy}
        </Typography>
      </Stack>

      <Dialog open={declineOpen} onClose={() => setDeclineOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t.declineTitle}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>{t.declineHelp}</Typography>
          <TextField
            fullWidth multiline minRows={3}
            label={t.reason}
            value={declineReason}
            onChange={e => setDeclineReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineOpen(false)} disabled={declining}>{t.cancel}</Button>
          <Button color="error" variant="contained" onClick={decline} disabled={declining}>
            {declining ? <CircularProgress size={20} /> : t.confirmDecline}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// ---------- Per-field renderer ----------

interface FieldRowProps {
  field: CeremonyField;
  locale: 'en' | 'fr';
  t: typeof T['en'];
  textValue: string;
  onTextChange: (v: string) => void;
  checkboxValue: boolean;
  onCheckboxChange: (v: boolean) => void;
  sigRef: (h: SignaturePadHandle | null) => void;
}

const FieldRow: React.FC<FieldRowProps> = ({
  field, locale, t, textValue, onTextChange, checkboxValue, onCheckboxChange, sigRef,
}) => {
  const baseLabel = field.label || (() => {
    switch (field.field_kind) {
      case 'signature': return t.sigLabel;
      case 'initials': return t.initialsLabel;
      case 'date': return t.dateLabel;
      case 'checkbox': return field.label || '';
      default: return t.textLabel;
    }
  })();

  if (field.field_kind === 'signature' || field.field_kind === 'initials') {
    return (
      <SignaturePad
        ref={(h) => sigRef(h)}
        label={baseLabel + (field.required ? ' *' : '')}
        width={field.field_kind === 'initials' ? 200 : 480}
        height={field.field_kind === 'initials' ? 100 : 160}
        clearLabel={locale === 'fr' ? 'Effacer' : 'Clear'}
      />
    );
  }

  if (field.field_kind === 'checkbox') {
    return (
      <FormControlLabel
        control={<Checkbox checked={checkboxValue} onChange={e => onCheckboxChange(e.target.checked)} />}
        label={baseLabel + (field.required ? ' *' : '')}
      />
    );
  }

  return (
    <TextField
      fullWidth
      label={baseLabel + (field.required ? ' *' : '')}
      value={textValue}
      onChange={e => onTextChange(e.target.value)}
      disabled={field.field_kind === 'date'}
      helperText={field.field_kind === 'date' ? (locale === 'fr' ? 'Date d’aujourd’hui — automatique' : 'Today’s date — auto-filled') : undefined}
    />
  );
};

export default SignerCeremony;
